import { useCallback, useSyncExternalStore } from 'react';

/**
 * Tema layar lapangan (T-P7-29, NFR-UX-04).
 *
 * Disimpan di `localStorage`, bukan IndexedDB terenkripsi: preferensi tema bukan
 * data nasabah, dan ia harus terbaca **sebelum** kunci dibuka — layar kunci pun
 * dipakai di bawah sinar matahari.
 */

export const THEMES = ['system', 'light', 'dark', 'sunlight'] as const;
export type Theme = (typeof THEMES)[number];

export const THEME_LABELS: Record<Theme, string> = {
  system: 'Ikuti sistem',
  light: 'Terang',
  dark: 'Gelap',
  sunlight: 'Sinar matahari',
};

const STORAGE_KEY = 'bssb.theme';
const listeners = new Set<() => void>();

function read(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  return (THEMES as readonly string[]).includes(stored ?? '') ? (stored as Theme) : 'system';
}

let current: Theme = 'system';

export function applyTheme(theme: Theme): void {
  current = theme;

  if (theme === 'system') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', theme);

  // Bilah status peramban ikut berubah; tanpa ini tepi atas layar tetap biru
  // terang di mode gelap dan terlihat seperti bug tampilan.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute(
      'content',
      theme === 'dark' ? '#1b2029' : theme === 'sunlight' ? '#ffffff' : '#1d4ed8',
    );

  for (const listener of listeners) listener();
}

export function setTheme(theme: Theme): void {
  localStorage.setItem(STORAGE_KEY, theme);
  applyTheme(theme);
}

/** Dipanggil sekali saat aplikasi dimuat, sebelum React merender apa pun. */
export function initTheme(): void {
  current = read();
  applyTheme(current);
}

export function useTheme(): [Theme, (theme: Theme) => void] {
  const theme = useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => current,
    () => 'system' as Theme,
  );

  return [theme, useCallback(setTheme, [])];
}
